import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Badge,
  Button,
  Empty,
  message,
  Select,
  Space,
  Table,
  Tag,
} from 'antd';
import {
  ClockCircleOutlined,
  EnvironmentOutlined,
  ExclamationCircleOutlined,
  LogoutOutlined,
  ReloadOutlined,
  ScheduleOutlined,
  UserDeleteOutlined,
  UserOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import moment from 'moment-jalaali';
import { useNavigate } from 'react-router-dom';
import { attendanceApi } from '../../../utils/api';
import { TableSkeleton } from '../../SkeletonLoaders';

const { Option } = Select;

const ISSUE_TYPES = [
  {
    key: 'all',
    label: 'همه موارد',
    countKey: 'total_issues',
    color: '#475569',
    bg: '#f1f5f9',
    icon: <WarningOutlined />,
  },
  {
    key: 'missing_clock_out',
    label: 'ورود بدون خروج',
    countKey: 'missing_clock_out',
    color: '#dc2626',
    bg: '#fef2f2',
    icon: <LogoutOutlined />,
  },
  {
    key: 'unauthorized_late',
    label: 'تأخیر غیرمجاز',
    countKey: 'unauthorized_late',
    color: '#ea580c',
    bg: '#fff7ed',
    icon: <ClockCircleOutlined />,
  },
  {
    key: 'out_of_location',
    label: 'تردد خارج از موقعیت',
    countKey: 'out_of_location',
    color: '#d97706',
    bg: '#fffbeb',
    icon: <EnvironmentOutlined />,
  },
  {
    key: 'unexcused_absence',
    label: 'غیبت بدون مرخصی',
    countKey: 'unexcused_absence',
    color: '#b91c1c',
    bg: '#fee2e2',
    icon: <UserDeleteOutlined />,
  },
  {
    key: 'shift_conflict',
    label: 'تداخل شیفت',
    countKey: 'shift_conflict',
    color: '#7c3aed',
    bg: '#f5f3ff',
    icon: <ScheduleOutlined />,
  },
];

const SEVERITY_TAG = {
  high: { color: 'error', label: 'خطر' },
  warning: { color: 'warning', label: 'هشدار' },
  info: { color: 'purple', label: 'اطلاع' },
};

const ACTION_LABELS = {
  manual_clock_out: 'ثبت خروج دستی',
  edit_attendance: 'ویرایش تردد',
  register_leave: 'ثبت مرخصی',
  approve_delay: 'تأیید تأخیر',
  verify_location: 'مشاهده موقعیت',
  approve_remote: 'تأیید دورکاری',
  mark_manual_attendance: 'ثبت تردد دستی',
  approve_overtime: 'تأیید اضافه‌کاری',
  edit_schedule: 'اصلاح شیفت',
  request_regularization: 'ثبت درخواست اصلاح',
  view_regularization: 'مشاهده درخواست اصلاح',
};

const EMPTY_COUNTS = {
  total_issues: 0,
  missing_clock_out: 0,
  unauthorized_late: 0,
  out_of_location: 0,
  unexcused_absence: 0,
  shift_conflict: 0,
};

const MONTH_OPTIONS = [
  { value: '01', label: 'فروردین' },
  { value: '02', label: 'اردیبهشت' },
  { value: '03', label: 'خرداد' },
  { value: '04', label: 'تیر' },
  { value: '05', label: 'مرداد' },
  { value: '06', label: 'شهریور' },
  { value: '07', label: 'مهر' },
  { value: '08', label: 'آبان' },
  { value: '09', label: 'آذر' },
  { value: '10', label: 'دی' },
  { value: '11', label: 'بهمن' },
  { value: '12', label: 'اسفند' },
];

const toFaDigits = (value) => {
  if (value == null || value === '') return '۰';
  return String(value).replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)]);
};

const typeMeta = (type) => ISSUE_TYPES.find((t) => t.key === type) || ISSUE_TYPES[0];

const AttendanceNeedsReviewView = ({
  departments = [],
  employees = [],
  year: yearProp,
  month: monthProp,
  onCountsChange,
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [type, setType] = useState('all');
  const [year, setYear] = useState(yearProp || moment().format('jYYYY'));
  const [month, setMonth] = useState(monthProp || moment().format('jM').padStart(2, '0'));
  const [departmentId, setDepartmentId] = useState('all');
  const [userId, setUserId] = useState('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [counts, setCounts] = useState(EMPTY_COUNTS);
  const [issues, setIssues] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, current_page: 1, last_page: 1, per_page: 15 });

  useEffect(() => {
    if (yearProp) setYear(String(yearProp));
  }, [yearProp]);

  useEffect(() => {
    if (monthProp) setMonth(String(monthProp).padStart(2, '0'));
  }, [monthProp]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await attendanceApi.getNeedsReview({
        type,
        year,
        month,
        department_id: departmentId,
        userId,
        page,
        per_page: pageSize,
      });
      const data = res?.data || res || {};
      const nextCounts = { ...EMPTY_COUNTS, ...(data.counts || {}) };
      setCounts(nextCounts);
      setIssues(Array.isArray(data.issues) ? data.issues : []);
      setPagination({
        total: data.pagination?.total ?? 0,
        current_page: data.pagination?.current_page ?? page,
        last_page: data.pagination?.last_page ?? 1,
        per_page: data.pagination?.per_page ?? pageSize,
      });
      onCountsChange?.(nextCounts);
    } catch (error) {
      console.error(error);
      message.error(error?.message || 'خطا در دریافت موارد نیازمند بررسی');
      setIssues([]);
    } finally {
      setLoading(false);
    }
  }, [type, year, month, departmentId, userId, page, pageSize, onCountsChange]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const handleTypeChange = (key) => {
    setType(key);
    setPage(1);
  };

  const handleAction = (action, record) => {
    const attendanceId = record?.details?.attendance_id;
    const empId = record?.user?.id;
    const date = record?.jalali_date || record?.date;

    switch (action) {
      case 'manual_clock_out':
      case 'edit_attendance':
      case 'mark_manual_attendance':
        navigate('/hr/attendance/register', {
          state: {
            preset: {
              userId: empId,
              attendanceId,
              date,
              action,
              issueId: record.id,
            },
          },
        });
        break;
      case 'register_leave':
        navigate('/hr/leaves/add', {
          state: {
            preset: {
              userId: empId,
              date,
              issueType: record.type,
            },
          },
        });
        break;
      case 'verify_location': {
        const coords = record?.details?.coordinates;
        if (coords) {
          const [lat, lng] = String(coords).split(',').map((s) => s.trim());
          if (lat && lng) {
            window.open(`https://www.google.com/maps?q=${lat},${lng}`, '_blank', 'noopener,noreferrer');
            break;
          }
        }
        message.info('مختصات موقعیت در دسترس نیست');
        break;
      }
      case 'request_regularization':
        navigate('/hr/attendance-regularizations', {
          state: {
            openCreate: true,
            tab: 'my_requests',
            preset: {
              attendance_id:
                attendanceId ||
                record.attendance_id ||
                record.details?.attendance_id ||
                null,
              jalali_date: record.jalali_date,
              date: record.date,
              recorded_clock_in_time:
                record.details?.clock_in_time ||
                record.details?.recorded_clock_in_time ||
                record.time ||
                null,
              proposed_clock_out_time:
                record.shift?.end_time || record.details?.shift_end || null,
              shift_end: record.shift?.end_time || null,
              type: 'clock_out',
            },
          },
        });
        break;
      case 'view_regularization':
        navigate('/hr/attendance-regularizations', {
          state: {
            tab: 'my_requests',
            highlightId:
              record.regularization?.id ||
              record.details?.regularization_id ||
              null,
          },
        });
        break;
      case 'approve_delay':
      case 'approve_remote':
      case 'approve_overtime':
        message.info('این مورد به‌صورت دستی در ویرایش تردد/شیفت قابل پیگیری است');
        break;
      case 'edit_schedule':
        navigate('/hr/shifts');
        break;
      default:
        message.info('عملیات در دسترس نیست');
    }
  };

  const yearOptions = useMemo(() => {
    const current = Number(moment().format('jYYYY'));
    return [current - 1, current, current + 1].map(String);
  }, []);

  const columns = [
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>کارمند</span>,
      key: 'user',
      width: 220,
      render: (_, record) => (
        <div className="flex items-center gap-2" style={{ fontFamily: 'CustomFont' }}>
          <Avatar
            size={36}
            src={record.user?.image_url || record.user?.image}
            icon={<UserOutlined />}
          />
          <div className="min-w-0">
            <div className="font-medium text-gray-800 dark:text-gray-100 truncate">
              {record.user?.name || '—'}
            </div>
            <div className="text-xs text-gray-500 truncate">
              {record.user?.department || '—'}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>نوع ناهنجاری</span>,
      key: 'type',
      width: 180,
      render: (_, record) => {
        const meta = typeMeta(record.type);
        const sev = SEVERITY_TAG[record.severity] || SEVERITY_TAG.warning;
        return (
          <div className="space-y-1" style={{ fontFamily: 'CustomFont' }}>
            <Tag
              icon={meta.icon}
              style={{
                color: meta.color,
                background: meta.bg,
                borderColor: 'transparent',
                margin: 0,
              }}
            >
              {record.type_title || meta.label}
            </Tag>
            <div>
              <Tag color={sev.color} className="m-0 text-[10px]">
                {sev.label}
              </Tag>
            </div>
          </div>
        );
      },
    },
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>تاریخ و ساعت</span>,
      key: 'datetime',
      width: 140,
      render: (_, record) => (
        <div style={{ fontFamily: 'CustomFont' }} className="text-sm">
          <div className="font-medium tabular-nums">{record.jalali_date || record.date || '—'}</div>
          <div className="text-xs text-gray-500">
            {record.time || record.details?.day_name || '—'}
          </div>
        </div>
      ),
    },
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>شیفت</span>,
      key: 'shift',
      width: 150,
      render: (_, record) => {
        const shift = record.shift;
        if (!shift) return <span className="text-gray-400">—</span>;
        return (
          <div style={{ fontFamily: 'CustomFont' }} className="text-sm">
            <div>{shift.name || '—'}</div>
            <div className="text-xs text-gray-500 tabular-nums">
              {(shift.start_time || '').slice(0, 5)}
              {shift.start_time || shift.end_time ? ' – ' : ''}
              {(shift.end_time || '').slice(0, 5)}
            </div>
          </div>
        );
      },
    },
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>توضیحات</span>,
      dataIndex: 'description',
      key: 'description',
      render: (text, record) => {
        const reg = record.regularization;
        const regStatusMap = {
          pending_manager: 'در انتظار مدیر',
          pending_hr: 'در انتظار منابع انسانی',
          approved: 'تأیید شده',
          rejected: 'رد شده',
          canceled: 'لغو شده',
        };
        const regStatus = reg?.status_label || regStatusMap[reg?.status] || null;
        return (
          <div style={{ fontFamily: 'CustomFont' }} className="text-sm space-y-1">
            <div className="text-gray-700 dark:text-gray-200">{text || '—'}</div>
            {reg ? (
              <Tag color="processing" className="m-0 text-[10px]">
                درخواست اصلاح: {regStatus || 'ثبت شده'}
              </Tag>
            ) : null}
          </div>
        );
      },
    },
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>عملیات</span>,
      key: 'actions',
      width: 220,
      fixed: 'left',
      render: (_, record) => {
        const actions = Array.isArray(record.suggested_actions) ? record.suggested_actions : [];
        if (!actions.length) return <span className="text-gray-400">—</span>;
        return (
          <Space wrap size={[4, 4]}>
            {actions.map((action) => (
              <Button
                key={action}
                size="small"
                type={action === actions[0] ? 'primary' : 'default'}
                danger={['manual_clock_out', 'mark_manual_attendance'].includes(action)}
                style={{ fontFamily: 'CustomFont', fontSize: 11 }}
                onClick={() => handleAction(action, record)}
              >
                {ACTION_LABELS[action] || action}
              </Button>
            ))}
          </Space>
        );
      },
    },
  ];

  return (
    <div style={{ fontFamily: 'CustomFont' }}>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 m-0 flex items-center gap-2">
            <ExclamationCircleOutlined className="text-amber-500" />
            نیازمند بررسی
            {counts.total_issues > 0 ? (
              <Badge
                count={toFaDigits(counts.total_issues)}
                style={{ backgroundColor: '#dc2626' }}
              />
            ) : null}
          </h2>
        </div>
        <Button
          icon={<ReloadOutlined />}
          onClick={fetchList}
          loading={loading}
          style={{ fontFamily: 'CustomFont' }}
        >
          بروزرسانی
        </Button>
      </div>

      {/* فیلترهای بازه و پرسنل */}
      <div className="flex flex-wrap gap-3 mb-4">
        <Select
          value={year}
          onChange={(v) => { setYear(v); setPage(1); }}
          style={{ width: 110, fontFamily: 'CustomFont' }}
        >
          {yearOptions.map((y) => (
            <Option key={y} value={y}>{y}</Option>
          ))}
        </Select>
        <Select
          value={month}
          onChange={(v) => { setMonth(v); setPage(1); }}
          style={{ width: 130, fontFamily: 'CustomFont' }}
        >
          {MONTH_OPTIONS.map((m) => (
            <Option key={m.value} value={m.value}>{m.label}</Option>
          ))}
        </Select>
        <Select
          value={departmentId}
          onChange={(v) => { setDepartmentId(v || 'all'); setPage(1); }}
          allowClear
          placeholder="دپارتمان"
          style={{ width: 180, fontFamily: 'CustomFont' }}
          showSearch
          optionFilterProp="children"
        >
          <Option value="all">همه دپارتمان‌ها</Option>
          {departments.map((d) => (
            <Option key={d.id} value={String(d.id)}>{d.team_name || d.name}</Option>
          ))}
        </Select>
        <Select
          value={userId}
          onChange={(v) => { setUserId(v || 'all'); setPage(1); }}
          allowClear
          placeholder="کارمند"
          style={{ width: 200, fontFamily: 'CustomFont' }}
          showSearch
          optionFilterProp="children"
        >
          <Option value="all">همه کارکنان</Option>
          {employees.map((e) => (
            <Option key={e.id} value={String(e.id)}>{e.name}</Option>
          ))}
        </Select>
      </div>

      {/* چیپ‌های دسته */}
      <div className="flex flex-wrap gap-2 mb-5">
        {ISSUE_TYPES.map((item) => {
          const count = counts[item.countKey] ?? 0;
          const active = type === item.key;
          return (
            <button
              key={item.key}
              type="button"
              onClick={() => handleTypeChange(item.key)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition-all border"
              style={{
                fontFamily: 'CustomFont',
                color: item.color,
                background: active ? item.bg : '#fff',
                borderColor: active ? item.color : '#e5e7eb',
                boxShadow: active ? `0 0 0 1px ${item.color}33` : undefined,
                fontWeight: active ? 700 : 500,
              }}
            >
              <span style={{ fontSize: 13 }}>{item.icon}</span>
              <span>{item.label}</span>
              <span
                className="tabular-nums text-xs px-1.5 py-0.5 rounded-full"
                style={{
                  background: active ? item.color : '#f3f4f6',
                  color: active ? '#fff' : '#6b7280',
                  minWidth: 22,
                  textAlign: 'center',
                }}
              >
                {toFaDigits(count)}
              </span>
            </button>
          );
        })}
      </div>

      {loading && !issues.length ? (
        <TableSkeleton rows={6} columns={5} />
      ) : (
        <Table
          rowKey={(r) => r.id || `${r.type}-${r.user?.id}-${r.date}`}
          columns={columns}
          dataSource={issues}
          loading={loading}
          scroll={{ x: 1100 }}
          pagination={{
            current: pagination.current_page || page,
            pageSize,
            total: pagination.total || 0,
            showSizeChanger: true,
            pageSizeOptions: ['10', '15', '30', '50'],
            showTotal: (total) => (
              <span style={{ fontFamily: 'CustomFont' }}>{`مجموع ${toFaDigits(total)} مورد`}</span>
            ),
            onChange: (p, size) => {
              setPage(p);
              if (size !== pageSize) {
                setPageSize(size);
                setPage(1);
              }
            },
          }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <span style={{ fontFamily: 'CustomFont' }}>
                    موردی برای بررسی در این بازه یافت نشد
                  </span>
                }
              />
            ),
          }}
          style={{ fontFamily: 'CustomFont' }}
          size="middle"
        />
      )}
    </div>
  );
};

export default AttendanceNeedsReviewView;
